import { getPlatformPreferences, updatePlatformPreferences } from '../hooks/api/preferences';
import type { UpdatablePlatformPreferences } from '../hooks/api/preferences';

const ONBOARDING_KEY = 'psi-onboarding-completed';

// Sync gate: tours must wait for this before deciding to run
let _onboardingSyncResolve: (() => void) | null = null;
let _onboardingSyncPromise: Promise<void> | null = null;

function resetSyncGate() {
  _onboardingSyncPromise = new Promise<void>((resolve) => {
    _onboardingSyncResolve = resolve;
  });
}
resetSyncGate();

function markSyncDone() {
  if (_onboardingSyncResolve) {
    _onboardingSyncResolve();
    _onboardingSyncResolve = null;
  }
}

/**
 * Returns a promise that resolves once onboarding preferences have been
 * synced from the API. Tours should await this before checking localStorage.
 */
export function waitForOnboardingSync(): Promise<void> {
  return _onboardingSyncPromise || Promise.resolve();
}

const TOUR_KEYS = [
  'psi-nav-tour-done',
  'psi-sessions-tour-done',
  'psi-calendar-tour-done',
  'psi-profile-tour-done',
] as const;

export const STEP_KEYS = {
  nav: 'psi-nav-tour-step',
  sessions: 'psi-sessions-tour-step',
  calendarConfig: 'psi-calendar-config-step',
  calendarBooking: 'psi-calendar-booking-step',
  profile: 'psi-profile-tour-step',
} as const;

export const getSavedStep = (key: string): number => {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? Number(v) : -1;
  } catch { return -1; }
};

export const saveStep = (key: string, step: number): void => {
  try { localStorage.setItem(key, String(step)); } catch { /* noop */ }
};

export const clearStep = (key: string): void => {
  try { localStorage.removeItem(key); } catch { /* noop */ }
};

/**
 * Devuelve true si el onboarding global ya fue completado
 * (todos los tours individuales finalizados).
 */
export const isOnboardingCompleted = (): boolean => {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Verifica si todos los tours individuales fueron completados.
 * Si es así, marca el onboarding global como completado en localStorage.
 * Cada tour individual mantiene su propio flag y no se vuelve a mostrar
 * aunque los demás no estén completados.
 */
export const checkAndMarkOnboardingComplete = (): void => {
  try {
    const allDone = TOUR_KEYS.every(key => localStorage.getItem(key) === '1');
    if (allDone) {
      localStorage.setItem(ONBOARDING_KEY, '1');
    }
  } catch {
    // noop
  }
};

/**
 * Trae las preferencias de onboarding desde la API y las guarda en localStorage.
 * Llamar después del login o al restaurar sesión.
 */
export async function syncOnboardingFromAPI(): Promise<void> {
  try {
    const prefs = await getPlatformPreferences();
    // DB false = "ya completó el tour" → marcar en localStorage como hecho
    if (!prefs.nav_onboarding) localStorage.setItem('psi-nav-tour-done', '1');
    if (!prefs.sessions_onboarding) localStorage.setItem('psi-sessions-tour-done', '1');
    if (!prefs.schedule_onboarding) localStorage.setItem('psi-calendar-tour-done', '1');
    if (!prefs.profile_onboarding) localStorage.setItem('psi-profile-tour-done', '1');
    if (!prefs.specific_day_onboarding) localStorage.setItem('psi-specific-day-tour-done', '1');
    if (!prefs.block_schedule_onboarding) localStorage.setItem('psi-block-schedule-tour-done', '1');
    if (!prefs.reschedule_slots_onboarding) localStorage.setItem('psi-reschedule-type-tour-done', '1');
    if (!prefs.attention_onboarding) localStorage.setItem('psi-attention-tour-done', '1');
    if (!prefs.vacations_onboarding) localStorage.setItem('psi-vacations-tour-done', '1');
    if (!prefs.metrics_onboarding) localStorage.setItem('psi-metrics-tour-done', '1');
    if (!prefs.patients_onboarding) localStorage.setItem('psi-patients-tour-done', '1');
    if (!prefs.first_time) {
      localStorage.setItem(ONBOARDING_KEY, '1');
    }
    // Cache del flag de primera sesión completada: si el DB ya lo tiene como true,
    // marcar el localStorage para que el calendario no vuelva a consultar la API.
    if (prefs.first_session_welcome_seen) {
      localStorage.setItem('psi-first-session-welcome-seen', '1');
    }
  } catch { /* silencioso — fallback a localStorage existente */ } finally {
    markSyncDone();
  }
}

/**
 * Reset the sync gate (call before a new sync, e.g. on login).
 */
export function resetOnboardingSyncGate(): void {
  resetSyncGate();
}

/**
 * Marca un tour como completado en el servidor.
 * Mapea el key de localStorage a la columna de la DB.
 */
export function markTourCompleteOnServer(tourKey: string): void {
  // Tipado con las claves que el PATCH acepta: si alguien mapea un tour a una columna
  // de solo lectura (ej. first_time), lo caza el compilador y no un 400 en runtime.
  const mapping: Record<string, keyof UpdatablePlatformPreferences> = {
    'psi-nav-tour-done': 'nav_onboarding',
    'psi-sessions-tour-done': 'sessions_onboarding',
    'psi-calendar-tour-done': 'schedule_onboarding',
    'psi-profile-tour-done': 'profile_onboarding',
    'psi-specific-day-tour-done': 'specific_day_onboarding',
    'psi-block-schedule-tour-done': 'block_schedule_onboarding',
    'psi-reschedule-type-tour-done': 'reschedule_slots_onboarding',
    'psi-attention-tour-done': 'attention_onboarding',
    'psi-vacations-tour-done': 'vacations_onboarding',
    'psi-metrics-tour-done': 'metrics_onboarding',
    'psi-patients-tour-done': 'patients_onboarding',
  };
  const column = mapping[tourKey];
  if (!column) return;
  updatePlatformPreferences({ [column]: false }).catch(() => { /* silencioso */ });
}
